import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { FaFire } from 'react-icons/fa';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useConfigActions from '../../../hooks/APIHooks/appointments/useConfigActions';
import useConversionActions from '../../../hooks/APIHooks/appointments/useConversionActions';
import useServiceActions from '../../../hooks/APIHooks/appointments/useServiceActions';
import useAssociateActions from '../../../hooks/APIHooks/users/useAssociateActions';
import useWeekDaysActions from '../../../hooks/APIHooks/useWeekDaysActions';
import { createEditConfigSchema } from '../../../schemas';
import { SelectData, WeekDay } from '../../../types/common';
import { Config, ConfigReqBody } from '../../../types/configurations';
import { showFullName } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import { showTimeIn12HrsFormat } from '../../../utils/timeAndDate';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCheckboxField from '../Inputs/AcademiaCheckboxField';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTimePicker from '../Inputs/AcademiaTimePicker';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditConfigModal = ({
  title,
  handleModalClose,
  config,
}: {
  title: 'create' | 'update';
  handleModalClose: () => void;
  config?: Config;
}) => {
  // integration of react hooks here
  const [weekDays, setWeekDays] = useState<Array<WeekDay>>([]);
  const [workingDays, setWorkingDays] = useState<Array<WeekDay>>([]);
  const [resetField, setResetField] = useState<boolean>(false);

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of API hooks here
  const { associateListHandler } = useAssociateActions('associateList');
  const { getServiceListHandler } = useServiceActions('serviceList');
  const { getMinuteConversionListHandler } =
    useConversionActions('conversionList');
  const { getWeekDaysHandler } = useWeekDaysActions();

  const { createNewConfigHandler, updateConfigHandler } = useConfigActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  console.log('WEEK DAYS: ', getWeekDaysHandler.data?.data);
  console.log('SERVICE LIST: ', getServiceListHandler.data?.data);

  // setting value of week days from database into state for create/update here
  useEffect(() => {
    if (getWeekDaysHandler.data?.data.length) {
      setWeekDays(getWeekDaysHandler.data.data);

      if (config) {
        const existingWorkingDays = getWeekDaysHandler.data.data.filter((day) =>
          config.working_days_with_names.some(
            (workingDay) => workingDay.value === day.value
          )
        );

        setWorkingDays(existingWorkingDays);

        setWeekDays((state) =>
          state.filter(
            (day) =>
              !existingWorkingDays.some(
                (workingDay) => workingDay.value === day.value
              )
          )
        );
      }
    }
  }, [getWeekDaysHandler.data, config]);

  // resetting the boolean state value to false after the field is changed here
  useEffect(() => {
    if (resetField) {
      setResetField(false);
    }
  }, [resetField]);

  // formatting dropdown options here
  const associateOptions = useMemo(() => {
    if (!associateListHandler.data?.data) return [];

    return associateListHandler.data.data.map((associate) => ({
      id: associate.id,
      name: showFullName(
        associate.first_name,
        associate.middle_name,
        associate.last_name
      ),
    }));
  }, [associateListHandler.data]);

  const serviceOptions = useMemo(() => {
    if (!getServiceListHandler.data?.data) return [];

    return getServiceListHandler.data.data.map((service) => ({
      id: service.id,
      name: service.name,
    }));
  }, [getServiceListHandler.data]);

  const serviceDescriptionOptions = useMemo(() => {
    if (!getServiceListHandler.data?.data) return [];

    return getServiceListHandler.data.data.map((service) =>
      service.is_most_popular ? (
        <FaFire title='Popular Right Now' className='text-base-orange' />
      ) : (
        <></>
      )
    );
  }, [getServiceListHandler.data]);

  const minConversionOptions = useMemo(() => {
    if (!getMinuteConversionListHandler.data?.data) return [];

    return getMinuteConversionListHandler.data.data.map((conversion) => ({
      id: conversion.id,
      name: conversion.name,
    }));
  }, [getMinuteConversionListHandler.data]);

  const minConversionDescriptionOptions = useMemo(() => {
    if (!getMinuteConversionListHandler.data?.data) return [];

    return getMinuteConversionListHandler.data.data.map(
      (conversion) => `${conversion.minutes_per_credit} Minutes/credit`
    );
  }, [getMinuteConversionListHandler.data]);

  // handler function to handle create/edit config form submission declared here
  const createEditConfigHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      let body: ConfigReqBody = {
        associate: values.associate.id,
        service: values.service.id,
        minute_conversion: values.minute_conversion.id,
        working_days: workingDays.map((workingDay) => workingDay.value),
        slot_duration: values.duration,
        lead_time: values.lead_time,
        finish_time: values.finish_time,
        appointment_buffer_time: values.buffer_time,
        allow_rescheduling: values.allow_rescheduling,
        reschedule_limit: values.allow_rescheduling
          ? values.reschedule_limit
          : 0,
      };

      console.log('CONFIG REQ BODY: ', body);

      if (!config) {
        body = {
          ...body,
          created_by: currentUser?.username as string,
        };

        await createNewConfigHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Configuration Created!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['config-list'] });
            } else {
              toast.error(res.message || 'Configuration Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body = {
          ...body,
          updated_by: currentUser?.username as string,
        };

        await updateConfigHandler
          .mutateAsync({ configId: config.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Configuration Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['config-list'] });
            } else {
              toast.error(res.message || 'Configuration Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  console.log('RESET FIELD VALUE: ', resetField);

  // function to add working day in array declared here
  const addWorkingDay = (value: SelectData) => {
    setWorkingDays((state) => [...state, { value: value.id, day: value.name }]);

    setWeekDays((state) => state.filter((day) => day.value !== value.id));
  };

  // function to remove working day from array declared here
  const removeWorkingDay = (workingDay: WeekDay) => {
    setWorkingDays((state) =>
      state.filter((day) => day.value !== workingDay.value)
    );

    setWeekDays((state) =>
      [...state, workingDay].sort((dayA, dayB) => dayA.value - dayB.value)
    );
  };

  // rendering create/update configuration modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText={`${title} configuration`} />

      <Formik
        initialValues={{
          associate: config
            ? { id: config.associate, name: config.associate_full_name }
            : SELECT_FIELD_DEFAULT_VALUE,
          duration: config?.slot_duration || '',
          service: config
            ? { id: config.service_data.id, name: config.service_data.name }
            : SELECT_FIELD_DEFAULT_VALUE,
          minute_conversion: config
            ? {
                id: config.minute_conversion_data.id,
                name: config.minute_conversion_data.name,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          lead_time: config?.lead_time || '',
          finish_time: config?.finish_time || '',
          buffer_time: config?.appointment_buffer_time || '',
          week_day: SELECT_FIELD_DEFAULT_VALUE,
          allow_rescheduling: config?.allow_rescheduling || false,
          reschedule_limit: config?.reschedule_limit || '',
        }}
        validationSchema={createEditConfigSchema}
        onSubmit={createEditConfigHandler}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaFormSelect
              name='associate'
              label='Select Associate'
              options={associateOptions}
              defaultValue={props.values.associate}
              showSearchInput
              isRequired
            />

            <AcademiaFormSelect
              name='service'
              label='Select Service'
              options={serviceOptions}
              defaultValue={props.values.service}
              optionDescription={serviceDescriptionOptions}
              showSearchInput
              isRequired
            />

            {workingDays.length ? (
              <div className='border-2 border-base-cyan px-2 py-3 rounded-lg flex flex-wrap gap-2'>
                {workingDays.map((workingDay) => (
                  <SkillBadge
                    key={workingDay.value}
                    badgeLabel={workingDay.day}
                    color='#388229'
                    isEditing
                    onClickRemove={() => removeWorkingDay(workingDay)}
                  />
                ))}
              </div>
            ) : null}

            <AcademiaFormSelect
              name='week_day'
              label='Select Working Days'
              options={weekDays.map((day) => ({
                id: day.value,
                name: day.day,
              }))}
              setValueOnChange={addWorkingDay}
              resetSelectField={resetField}
              setResetSelectField={() => setResetField(true)}
              defaultValue={SELECT_FIELD_DEFAULT_VALUE}
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='duration'
              label='Slot Duration (Min.)'
              placeholder='Enter configuration slot duration here...'
              min={10}
              isRequired
            />

            <div className='grid md:grid-cols-2 gap-4'>
              <AcademiaTimePicker
                name='lead_time'
                label='Starting Time'
                placeholder='Starting time here...'
                defaultTime={
                  config?.lead_time
                    ? showTimeIn12HrsFormat(config.lead_time as string)
                    : undefined
                }
                isRequired
              />

              <AcademiaTimePicker
                name='finish_time'
                label='Finishing Time'
                placeholder='Finishing time here...'
                defaultTime={
                  config?.finish_time
                    ? showTimeIn12HrsFormat(config.finish_time as string)
                    : undefined
                }
                isRequired
              />
            </div>

            <AcademiaFormSelect
              name='minute_conversion'
              label='Select Service Rate'
              options={minConversionOptions}
              defaultValue={props.values.minute_conversion}
              optionDescription={minConversionDescriptionOptions}
              showSearchInput
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='buffer_time'
              label='Buffer Time (Min.)'
              placeholder='Enter configuration buffer time here...'
              min={2}
              isRequired
            />

            <AcademiaCheckboxField
              name='allow_rescheduling'
              label='Allow rescheduling?'
            />

            <AnimatePresence
              initial={false}
              mode='wait'
              onExitComplete={() => null}
            >
              {props.values.allow_rescheduling ? (
                <motion.div
                  initial={{ y: -50, opacity: 0, height: 0 }}
                  animate={{ y: 0, opacity: 1, height: 'fit-content' }}
                  exit={{ y: -50, opacity: 0, height: 0 }}
                >
                  <AcademiaInputField
                    type='number'
                    name='reschedule_limit'
                    label='Appointment Reschedule Limit'
                    placeholder='Enter reschedule limit here...'
                    max={5}
                    isRequired
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>

            <div className='!mt-6'>
              <AcademiaButton
                title={
                  props.values.associate.id === -1 ||
                  props.values.service.id === -1 ||
                  props.values.minute_conversion.id === -1 ||
                  !workingDays.length
                    ? 'Please, select an associate, a service, a service rate & some working days first!'
                    : ''
                }
                type='submit'
                variant='premium'
                icon={
                  title === 'create' ? (
                    <TbSquareRoundedPlusFilled className='size-6' />
                  ) : (
                    <RxUpdate className='size-6' />
                  )
                }
                isDisabled={
                  props.values.associate.id === -1 ||
                  props.values.service.id === -1 ||
                  props.values.minute_conversion.id === -1 ||
                  !workingDays.length
                }
                isLoading={props.isSubmitting}
                fullWidth
              >
                {title} Configuration
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditConfigModal;
